import { stardust } from '@eidellev/adonis-stardust/client';

const STAGING_HOST = 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com';
const LIVE_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'];
const SHARED_KEY = '__raffiCartAvailability20261022';
const NOTICE_KEY = 'raffi-cart-availability-notice-20261022';
const REQUEST_MS = 6000;
const TOTAL_MS = 12000;
export type CartAvailabilityStatus = 'unchanged' | 'cleaned' | 'check-unavailable' | 'remove-failed';
export type CartAvailabilityResult = { cart: any; status: CartAvailabilityStatus; removed: boolean; removedCount: number; blockedVariantIds: string[] };
export type CartAvailabilityOptions = { storeKey?: string; endpoint?: string };
type FlightState = { promise?: Promise<CartAvailabilityResult>; blocked: string[]; orphanLineIds: string[]; unsafe: boolean };

/** Approved public release; private staging still requires its preview marker. Protected page UI stays unchanged. */
export function cartAvailabilityEnabled(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const host = window.location.hostname;
  const approved = LIVE_HOSTS.includes(host) || host === STAGING_HOST && document.querySelector('meta[name="raffi-native-preview"]')?.getAttribute('content') === '20261008';
  if (!approved || /(?:^|[\/_-])(?:rolex|tag[\s_-]*heuer)(?:[\/_-]|$)/i.test(window.location.pathname)) return false;
  const product = document.querySelector<HTMLElement>('main.product .js-product, main[data-watch-buying="product"] .js-product, main[data-jewellery-buying="product"] .js-product');
  return !/rolex|tag[\s_-]*heuer/i.test(product?.dataset.brand || '');
}
const validCart = (cart: any): boolean => typeof cart?.id === 'string' && !!cart.id && Array.isArray(cart.lineItems);
const variantId = (line: any): string => /^gid:\/\/shopify\/ProductVariant\/[1-9]\d*$/.test(line?.variant?.id || '') ? line.variant.id : '';
const attributesKey = (line: any): string => JSON.stringify((line.customAttributes || []).map((attribute: any) => [String(attribute.key), String(attribute.value)]).sort((a: string[], b: string[]) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1])));
const unchangedLine = (before: any, after: any): boolean => !!after && before.id === after.id && variantId(before) === variantId(after) && Number(before.quantity) === Number(after.quantity) && attributesKey(before) === attributesKey(after);
function deadline<T>(operation: Promise<T>, milliseconds: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Cart availability timed out')), milliseconds);
    operation.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
  });
}
function unavailable(cart: any, state: FlightState): CartAvailabilityResult {
  // A known blocked removal failure remains unsafe until a new successful check clears it.
  return { cart, status: state.unsafe ? 'remove-failed' : 'check-unavailable', removed: false, removedCount: 0, blockedVariantIds: [...state.blocked] };
}
function genericLog() { console.warn('Shopping bag availability check could not be completed.'); }

/** No eligibility cache, shopper storage, UI, tracking or cart creation. The caller owns notices and navigation. */
export async function checkCartAvailability(cart: any, client: any, options: CartAvailabilityOptions = {}): Promise<CartAvailabilityResult> {
  const unchanged: CartAvailabilityResult = { cart, status: 'unchanged', removed: false, removedCount: 0, blockedVariantIds: [] };
  if (!cartAvailabilityEnabled() || !validCart(cart)) return unchanged;
  // Both app and bag entries compile this module separately, so share the actual promise on window.
  const sharedWindow = window as any;
  const store: Map<string, FlightState> = sharedWindow[SHARED_KEY] || (sharedWindow[SHARED_KEY] = new Map());
  const key = JSON.stringify([options.storeKey || '', cart.id]);
  let state = store.get(key);
  if (!state) { state = { blocked: [], orphanLineIds: [], unsafe: false }; store.set(key, state); }
  if (state.promise) return state.promise;
  const active = state;
  active.promise = (async (): Promise<CartAvailabilityResult> => {
    let latest = cart;
    let blocked: string[] = [];
    let orphans: any[] = [];
    const expires = Date.now() + TOTAL_MS;
    const bounded = <T>(operation: () => Promise<T>): Promise<T> => {
      const remaining = expires - Date.now();
      if (remaining <= 0) return Promise.reject(new Error('Cart availability timed out'));
      return deadline(Promise.resolve().then(operation), Math.min(REQUEST_MS, remaining));
    };
    try {
      const fresh: any = await bounded(() => client.cart.fetch(cart.id));
      if (!validCart(fresh) || fresh.id !== cart.id) throw new Error('Cart unavailable');
      latest = fresh;
      // A shopper may have manually removed the previously unsafe line. Keep
      // the failure latch only for known bad lines still in this fresh cart.
      active.blocked = active.blocked.filter(id => latest.lineItems.some((line: any) => variantId(line) === id));
      active.orphanLineIds = active.orphanLineIds.filter(id => latest.lineItems.some((line: any) => line.id === id && line.variant == null));
      active.unsafe = !!(active.blocked.length || active.orphanLineIds.length);
      orphans = latest.lineItems.filter((line: any) => line && line.variant == null);
      active.orphanLineIds = orphans.map((line: any) => line.id).filter(Boolean);
      if (orphans.length) active.unsafe = true;
      const ids = Array.from(new Set<string>(latest.lineItems.map(variantId).filter(Boolean)));
      if (!ids.length && !orphans.length) {
        active.blocked = []; active.orphanLineIds = []; active.unsafe = false;
        return { ...unchanged, cart: latest };
      }
      if (ids.length) {
      const endpoint = new URL(options.endpoint || stardust.route('api.shop.cartCheck'), window.location.origin);
      if (endpoint.origin !== window.location.origin || endpoint.username || endpoint.password) throw new Error('Cart availability endpoint invalid');
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || document.querySelector<HTMLInputElement>('input[name="_csrf"]')?.value || '';
      if (!csrf.trim()) throw new Error('Cart availability token unavailable');
      for (let offset = 0; offset < ids.length; offset += 250) {
      const chunk = ids.slice(offset, offset + 250);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), Math.min(REQUEST_MS, expires - Date.now()));
      let response: Response;
      try {
        response = await bounded(() => fetch(endpoint.href, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf }, body: JSON.stringify({ variantIds: chunk }), signal: controller.signal }));
        if (!response.ok) throw new Error('Cart availability request unavailable');
        const body = await bounded(() => response.json());
        if (!Array.isArray(body?.blocked) || body.blocked.some((id: any) => typeof id !== 'string' || !chunk.includes(id))) throw new Error('Cart availability response invalid');
        blocked = [...new Set<string>([...blocked, ...body.blocked])];
        // Later chunk failure must not turn already-confirmed blocked lines into a fail-open checkout.
        active.blocked = [...new Set<string>([...active.blocked.filter(id => !chunk.includes(id)), ...body.blocked])];
        active.unsafe = !!(active.blocked.length || orphans.length);
      } finally { clearTimeout(timer); }
      }
      }
      active.blocked = blocked; active.unsafe = !!(blocked.length || orphans.length);
      if (!active.unsafe) return { ...unchanged, cart: latest };
    } catch {
      genericLog();
      return unavailable(latest, active);
    }

    const removals = latest.lineItems.filter((line: any) => orphans.includes(line) || blocked.includes(variantId(line)));
    const retained = latest.lineItems.filter((line: any) => !removals.includes(line));
    let confirmedRemovals = 0;
    try {
      if (!removals.length || removals.some((line: any) => typeof line.id !== 'string' || !line.id)) throw new Error('Cart line unavailable');
      // The installed wrapper accepts ID arrays. Never replay a write, even
      // when its old formatter returns null after Shopify applied the change.
      for (let offset = 0; offset < removals.length; offset += 250) {
        const ids = removals.slice(offset, offset + 250).map((line: any) => line.id).filter((id: string) => latest.lineItems.some((line: any) => line.id === id));
        if (ids.length) {
          try { await bounded(() => client.cart.removeLineItems(cart.id, ids)); }
          catch { /* Only the authoritative read below can confirm an ambiguous outcome. */ }
        }
        const confirmed: any = await bounded(() => client.cart.fetch(cart.id));
        if (!validCart(confirmed) || confirmed.id !== cart.id) throw new Error('Cart removal unconfirmed');
        latest = confirmed;
        confirmedRemovals = removals.filter((line: any) => !latest.lineItems.some((item: any) => item.id === line.id)).length;
        if (ids.some((id: string) => latest.lineItems.some((line: any) => line.id === id))) throw new Error('Cart removal unconfirmed');
      }
      // Mutation return values may include only the SDK's first ten lines.
      // Always verify the complete authoritative cart after all removals.
      const final: any = await bounded(() => client.cart.fetch(cart.id));
      if (!validCart(final) || final.id !== cart.id) throw new Error('Cart removal unconfirmed');
      latest = final;
      confirmedRemovals = removals.filter((line: any) => !latest.lineItems.some((item: any) => item.id === line.id)).length;
      // Removing one line must preserve all legitimate quantities and custom selections.
      if (latest.lineItems.some((line: any) => blocked.includes(variantId(line))) ||
          retained.some((line: any) => !unchangedLine(line, latest.lineItems.find((item: any) => item.id === line.id))) ||
          latest.lineItems.some((line: any) => !retained.some((item: any) => unchangedLine(item, line)))) throw new Error('Cart contents not confirmed');
      active.blocked = []; active.orphanLineIds = []; active.unsafe = false;
      return { cart: latest, status: 'cleaned', removed: true, removedCount: removals.length, blockedVariantIds: blocked };
    } catch {
      genericLog();
      return { cart: latest, status: 'remove-failed', removed: confirmedRemovals > 0, removedCount: confirmedRemovals, blockedVariantIds: blocked };
    }
  })();
  try { return await active.promise; }
  finally {
    delete active.promise;
    if (!active.unsafe) store.delete(key);
  }
}

/** Checkout may keep a cleaned bag only when the actual selected variant survived. */
export function cartAvailabilityAllowsCheckout(result: CartAvailabilityResult, selectedVariantId?: string): boolean {
  if (result.status === 'remove-failed' || !result.cart?.lineItems?.length || result.cart.lineItems.some((line: any) => !line.variant?.id || line.variant.availableForSale === false)) return false;
  return !selectedVariantId || result.cart.lineItems.some((line: any) => line.variant?.id === selectedVariantId && Number(line.quantity) > 0);
}
export function recordCartAvailabilityNotice(cartId: string, storeKey = ''): void {
  if (!cartAvailabilityEnabled()) return;
  try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify([storeKey, cartId])); } catch { /* The visible notice must still work without storage. */ }
}
export function consumeCartAvailabilityNotice(cartId: string | null, storeKey = ''): boolean {
  if (!cartAvailabilityEnabled() || !cartId) return false;
  try {
    if (sessionStorage.getItem(NOTICE_KEY) !== JSON.stringify([storeKey, cartId])) return false;
    sessionStorage.removeItem(NOTICE_KEY); return true;
  } catch { return false; }
}
